'use client'

import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  MAX_USER_UPLOAD_FILES,
  validateUserAttachment,
} from '@/lib/core/media/image-upload-support'
import { noop } from '@/lib/shared/noop'
import { appToast } from '@/lib/shared/toast'

import {
  type AttachmentsContext,
  isAttachmentDescriptor,
  isUploadedAttachment,
  normalizeAttachmentInput,
  type PromptInputAttachment,
  type PromptInputAttachmentInput,
  type PromptInputAttachmentUploadBehavior,
  scheduleAttachmentUploads,
} from './attachments'

export type TextInputContext = {
  value: string
  setInput: (value: string) => void
  clear: () => void
}

export type PromptInputMentionItem = {
  id: string
  searchValue: string
  label: ReactNode
  icon?: ReactNode
  tooltip?: ReactNode
}

export type PromptInputControllerProps = {
  textInput: TextInputContext
  attachments: AttachmentsContext
  __registerFileInput: (
    ref: RefObject<HTMLInputElement | null>,
    open: () => void
  ) => void
}

const PromptInputController = createContext<PromptInputControllerProps | null>(
  null
)
const ProviderAttachmentsContext = createContext<AttachmentsContext | null>(
  null
)
export const LocalAttachmentsContext = createContext<AttachmentsContext | null>(
  null
)

export const usePromptInputController = () => {
  const ctx = useContext(PromptInputController)
  if (!ctx) {
    throw new Error(
      'Wrap your component inside <PromptInputProvider> to use usePromptInputController().'
    )
  }
  return ctx
}

export const useOptionalPromptInputController = () =>
  useContext(PromptInputController)

export const useProviderAttachments = () => {
  const ctx = useContext(ProviderAttachmentsContext)
  if (!ctx) {
    throw new Error(
      'Wrap your component inside <PromptInputProvider> to use useProviderAttachments().'
    )
  }
  return ctx
}

export const useOptionalProviderAttachments = () =>
  useContext(ProviderAttachmentsContext)

export const usePromptInputAttachments = () => {
  const provider = useOptionalProviderAttachments()
  const local = useContext(LocalAttachmentsContext)
  const context = provider ?? local
  if (!context) {
    throw new Error(
      'usePromptInputAttachments must be used within a PromptInput or PromptInputProvider'
    )
  }
  return context
}

export type PromptInputProviderProps = PropsWithChildren<{
  initialInput?: string
  initialAttachments?: Array<PromptInputAttachmentInput>
  attachmentUploadBehavior?: PromptInputAttachmentUploadBehavior
}>

export function PromptInputProvider({
  initialInput: initialTextInput = '',
  initialAttachments = [],
  attachmentUploadBehavior = 'upload',
  children,
}: PromptInputProviderProps) {
  const [textInput, setTextInput] = useState(initialTextInput)
  const clearInput = useCallback(() => setTextInput(''), [])

  const [attachmentsState, setAttachmentsState] = useState<
    Array<PromptInputAttachment>
  >(() =>
    initialAttachments.map((attachment) =>
      normalizeAttachmentInput(attachment, attachmentUploadBehavior)
    )
  )
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const openRef = useRef<() => void>(noop)
  const hydratedInitialAttachments = useRef(false)

  useEffect(() => {
    if (hydratedInitialAttachments.current || initialAttachments.length === 0) {
      return
    }

    hydratedInitialAttachments.current = true
    scheduleAttachmentUploads({
      items: attachmentsState,
      inputs: initialAttachments,
      uploadBehavior: attachmentUploadBehavior,
      update: setAttachmentsState,
    })
  }, [attachmentUploadBehavior, attachmentsState, initialAttachments])

  const add = useCallback(
    (files: Array<PromptInputAttachmentInput> | FileList) => {
      const incoming = Array.from(
        files as ArrayLike<PromptInputAttachmentInput>
      ).filter((input) => {
        if (isUploadedAttachment(input)) {
          return true
        }
        // Synthetic HTML/preview descriptors are app-generated, not
        // user-picked files — they bypass upload validation.
        if (
          isAttachmentDescriptor(input) &&
          (input.previewText || input.isHtmlAttachment)
        ) {
          return true
        }
        const file = isAttachmentDescriptor(input) ? input.file : input
        const validation = validateUserAttachment(file)
        if (!validation.ok) {
          appToast.error(validation.reason)
          return false
        }
        return true
      })
      if (incoming.length === 0) {
        return
      }

      const availableSlots = Math.max(
        0,
        MAX_USER_UPLOAD_FILES - attachmentsState.length
      )
      const capped = incoming.slice(0, availableSlots)
      if (incoming.length > capped.length) {
        appToast.error(`You can attach up to ${MAX_USER_UPLOAD_FILES} files`)
      }
      if (capped.length === 0) {
        return
      }

      const nextItems = capped.map((file) =>
        normalizeAttachmentInput(file, attachmentUploadBehavior)
      )
      setAttachmentsState((prev) => prev.concat(nextItems))
      scheduleAttachmentUploads({
        items: nextItems,
        inputs: capped,
        uploadBehavior: attachmentUploadBehavior,
        update: setAttachmentsState,
      })
    },
    [attachmentUploadBehavior, attachmentsState.length]
  )

  const remove = useCallback((id: string) => {
    setAttachmentsState((prev) => {
      const found = prev.find((file) => file.id === id)
      if (found?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(found.url)
      }
      return prev.filter((file) => file.id !== id)
    })
  }, [])

  const clear = useCallback(() => {
    setAttachmentsState((prev) => {
      for (const file of prev) {
        if (file.url?.startsWith('blob:')) {
          URL.revokeObjectURL(file.url)
        }
      }
      return []
    })
  }, [])

  const openFileDialog = useCallback(() => {
    openRef.current?.()
  }, [])

  const attachments = useMemo<AttachmentsContext>(
    () => ({
      files: attachmentsState,
      add,
      remove,
      clear,
      isUploading: attachmentsState.some(
        (attachment) => attachment.uploadStatus === 'uploading'
      ),
      hasUploadError: attachmentsState.some(
        (attachment) => attachment.uploadStatus === 'error'
      ),
      openFileDialog,
      fileInputRef,
    }),
    [attachmentsState, add, remove, clear, openFileDialog]
  )

  const __registerFileInput = useCallback(
    (ref: RefObject<HTMLInputElement | null>, open: () => void) => {
      fileInputRef.current = ref.current
      openRef.current = open
    },
    []
  )

  const controller = useMemo<PromptInputControllerProps>(
    () => ({
      textInput: {
        value: textInput,
        setInput: setTextInput,
        clear: clearInput,
      },
      attachments,
      __registerFileInput,
    }),
    [textInput, clearInput, attachments, __registerFileInput]
  )

  return (
    <PromptInputController.Provider value={controller}>
      <ProviderAttachmentsContext.Provider value={attachments}>
        {children}
      </ProviderAttachmentsContext.Provider>
    </PromptInputController.Provider>
  )
}
